/**
 * Per-Band Envelope Analysis
 *
 * Splits an active-sonar ping into frequency bands (L / M / H) via a shared
 * {@link BandedCorrelation} input (so we don't repeat bandpass + correlate
 * work that other consumers already did), and runs a Hilbert-envelope
 * analysis on each band's correlation. Interference fringes inside an echo
 * blob carry shape information across frequency, so a per-band envelope
 * feature is a richer descriptor than a single wideband envelope.
 *
 * Pure-function module — no I/O, no global state.
 */

import type { BandedCorrelation } from './band-correlations.js';
import { computeEnvelopeAnalysis } from '../dsp/hilbert-envelope.js';

/** Per-band envelope analysis output. */
export interface PerBandEnvelope {
  /** Band identifier (e.g. 'L', 'M', 'H'). */
  id: string;
  /** Lower cutoff (Hz). */
  fLow: number;
  /** Upper cutoff (Hz). */
  fHigh: number;
  /** Per-band correlation envelope (same length as wideband corr). */
  envelope: Float32Array;
  /** argmax of envelope. -1 if envelope is empty. */
  peakIndex: number;
  /** envelope[peakIndex]. 0 if envelope is empty. */
  peakValue: number;
  /** Phase-coherence quality metric in [0, 1]. */
  phaseCoherence: number;
  /** Range (m) corresponding to peakIndex via the profile.ts mapping. */
  rangeAtPeak: number;
}

/** Aggregate result across all bands. */
export interface PerBandEnvelopeResult {
  /** One entry per band, in input order. */
  bands: PerBandEnvelope[];
  /** Per-band peakValue values — useful for cross-band fusion / classification. */
  peakValues: Float32Array;
  /** Index of the band with the highest peakValue. -1 if no bands or all peakValues are zero. */
  dominantBandIdx: number;
}

/** Arguments for {@link computePerBandEnvelopes}. */
export interface ComputePerBandEnvelopesArgs {
  /** Pre-computed per-band bandpass correlations (from `computeBandCorrelations`). */
  bandCorrelations: BandedCorrelation[];
  /** Sample rate (Hz). */
  sampleRate: number;
  /** Correlation tau offset (matches `CorrelationResult.tau0`). */
  tau0: number;
  /** Speed of sound in m/s. */
  speedOfSound: number;
  /** Half-width of phase-coherence window (samples). Forwarded to hilbert. Default 8. */
  coherenceWindowSamples?: number;
}

/**
 * Compute per-band Hilbert envelopes for an active-sonar ping.
 *
 * For each input band entry:
 *   1. Run computeEnvelopeAnalysis on its real (and optional imag) correlation.
 *   2. Map peakIndex → range using the same convention as
 *      buildRangeProfileFromCorrelation (`tau = i/sampleRate - tau0`,
 *      `range = c * tau / 2`).
 */
export function computePerBandEnvelopes(
  args: ComputePerBandEnvelopesArgs,
): PerBandEnvelopeResult {
  const {
    bandCorrelations,
    sampleRate,
    tau0,
    speedOfSound,
    coherenceWindowSamples = 8,
  } = args;

  // Degenerate inputs: produce empty result.
  if (bandCorrelations.length === 0) {
    return {
      bands: [],
      peakValues: new Float32Array(0),
      dominantBandIdx: -1,
    };
  }

  const inputsValid =
    sampleRate > 0 &&
    Number.isFinite(sampleRate) &&
    speedOfSound > 0 &&
    Number.isFinite(speedOfSound);

  const out: PerBandEnvelope[] = [];
  const peakValues = new Float32Array(bandCorrelations.length);

  for (let i = 0; i < bandCorrelations.length; i++) {
    const entry = bandCorrelations[i];

    // Defensive: if inputs are invalid or the correlation is empty, emit a
    // zero entry of the right shape so downstream length checks stay sane.
    if (!inputsValid || entry.corrReal.length === 0) {
      out.push({
        id: entry.id,
        fLow: entry.fLow,
        fHigh: entry.fHigh,
        envelope: new Float32Array(0),
        peakIndex: -1,
        peakValue: 0,
        phaseCoherence: 0,
        rangeAtPeak: 0,
      });
      peakValues[i] = 0;
      continue;
    }

    const env = computeEnvelopeAnalysis({
      corrReal: entry.corrReal,
      corrImag: entry.corrImag,
      coherenceWindowSamples,
    });

    let rangeAtPeak = 0;
    if (env.peakIndex >= 0) {
      // Mirror buildRangeProfileFromCorrelation convention:
      //   tau = i/sampleRate - tau0
      //   R   = c * tau / 2
      const tau = env.peakIndex / sampleRate - tau0;
      rangeAtPeak = (speedOfSound * tau) / 2;
    }

    out.push({
      id: entry.id,
      fLow: entry.fLow,
      fHigh: entry.fHigh,
      envelope: env.envelope,
      peakIndex: env.peakIndex,
      peakValue: env.peakValue,
      phaseCoherence: env.phaseCoherence,
      rangeAtPeak,
    });
    peakValues[i] = env.peakValue;
  }

  // Find dominant band. If all peakValues are zero, dominantBandIdx = -1
  // (previous behaviour returned 0 misleadingly).
  let dominantBandIdx = -1;
  let bestPeak = 0;
  for (let i = 0; i < peakValues.length; i++) {
    if (peakValues[i] > bestPeak) {
      bestPeak = peakValues[i];
      dominantBandIdx = i;
    }
  }
  if (out.length === 0) dominantBandIdx = -1;

  return {
    bands: out,
    peakValues,
    dominantBandIdx,
  };
}
